import React from 'react'

import {returnRefundMethods} from '../../../../utils/variables.js'
import {formSpacing, Check, FormRow, FormStack, Input, Select, TextArea} from '../../../components/UI/forms/index.js'
import PickupPointMap from './PickupPointMap.js'
import {CARRIER_LOGOS, TEXT, OptionTile, Panel, StepTitle, carrierDisplayName, formatCosts} from './ui.js'

// Steps of a new return request. Each step renders its part of the returnParcelRequest and has an
// isValid(request, portal) check that enables the "next" button.

export const DetailsStep = {
    key: 'details',
    label: 'Gegevens',
    isValid: ({address}) => !!(address.name && address.postalCode && address.nr && address.street && address.city && /\S+@\S+\.\S+/.test(address.email || '')),
    Component: function DetailsStep({request, onChange}) {
        const {address} = request

        // The country can't be changed: shipping options and costs are based on it
        const field = (key, label, props = {}) => (
            <Input
                label={label}
                value={address[key] || ''}
                onChange={(event) => onChange({...request, address: {...address, [key]: event.target.value}})}
                {...props}
            />
        )

        return (
            <>
                <StepTitle title='Jouw gegevens' subtitle='Controleer je adres, dit wordt het afzendadres van je retourzending.' />

                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    {field('name', 'Naam')}

                    <FormRow>
                        {field('postalCode', 'Postcode', {toUpperCase: true, style: {flex: 2}})}
                        {field('nr', 'Huisnummer')}
                        {field('addition', 'Toevoeging')}
                    </FormRow>

                    {field('street', 'Straat')}
                    {field('city', 'Plaats')}
                    {field('email', 'E-mailadres', {type: 'email'})}
                    {field('phone', 'Telefoonnummer (optioneel)', {type: 'tel'})}
                </FormStack>
            </>
        )
    }
}

export const ItemsStep = {
    key: 'items',
    label: 'Items',
    isValid: ({items}) => items.some((item) => item.return),
    Component: function ItemsStep({request, portal, onChange}) {
        const {items} = request

        const updateItem = (index, changes) => {
            onChange({...request, items: items.map((item, i) => i === index ? {...item, ...changes} : item)})
        }

        return (
            <>
                <StepTitle title='Wat wil je retourneren?' subtitle='Selecteer de items en geef aan waarom je ze terugstuurt.' />

                {items.length > 1 &&
                    <div style={{padding: '0 4px 8px'}}>
                        <Check
                            label='Alles selecteren'
                            checked={items.every((item) => item.return)}
                            indeterminate={items.some((item) => item.return)}
                            onChange={(event) => onChange({...request, items: items.map((item) => ({...item, return: event.target.checked}))})}
                        />
                    </div>
                }

                {items.map((item, index) => (
                    <Panel key={index} style={item.return ? {borderColor: TEXT.muted} : {}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                            <div style={{flex: 1}}>
                                <Check
                                    label={item.description}
                                    checked={item.return}
                                    onChange={(event) => updateItem(index, {return: event.target.checked})}
                                />
                            </div>
                            <span style={{fontSize: 13, color: TEXT.muted}}>{item.quantity}×</span>
                        </div>

                        {item.return &&
                            <div className='rp-fade-in' style={{marginTop: 12}}>
                                <FormStack>
                                    {item.quantity > 1 &&
                                        <Input
                                            type='number'
                                            label='Aantal'
                                            value={item.returnQuantity}
                                            min={1}
                                            max={item.quantity}
                                            onChange={(event) => updateItem(index, {returnQuantity: parseInt(event.target.value)})}
                                        />
                                    }

                                    {!!portal.returnReasons.length &&
                                        <Select
                                            label='Reden van retour'
                                            value={item.returnReason}
                                            onChange={(event) => updateItem(index, {returnReason: event.target.value, returnReasonComment: ''})}
                                        >
                                            {portal.returnReasons.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                                        </Select>
                                    }

                                    {item.returnReason === 'Anders:' &&
                                        <TextArea
                                            rows={3}
                                            label='Toelichting'
                                            value={item.returnReasonComment}
                                            onChange={(event) => updateItem(index, {returnReasonComment: event.target.value})}
                                        />
                                    }
                                </FormStack>
                            </div>
                        }
                    </Panel>
                ))}
            </>
        )
    }
}

export const RefundStep = {
    key: 'refund',
    label: 'Terugbetaling',
    isValid: ({refundMethod, refundMethodComment}) => !!refundMethod && (refundMethod !== 'exchange' || !!refundMethodComment),
    Component: function RefundStep({request, portal, color, onChange}) {
        return (
            <>
                <StepTitle title='Hoe wil je het geregeld hebben?' />

                {portal.returnRefundMethods.map((refundMethod) => (
                    <OptionTile
                        key={refundMethod}
                        color={color}
                        selected={request.refundMethod === refundMethod}
                        onClick={() => onChange({...request, refundMethod})}
                    >
                        <div style={{fontWeight: 500, color: TEXT.strong}}>{returnRefundMethods[refundMethod].title}</div>
                        <div style={{fontSize: 13, color: TEXT.muted}}>{returnRefundMethods[refundMethod].info}</div>
                    </OptionTile>
                ))}

                {request.refundMethod === 'exchange' &&
                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        <TextArea
                            rows={3}
                            label='Waarvoor wil je ruilen?'
                            value={request.refundMethodComment}
                            onChange={(event) => onChange({...request, refundMethodComment: event.target.value})}
                        />
                    </FormStack>
                }
            </>
        )
    }
}

export const ShippingStep = {
    key: 'shipping',
    label: 'Verzending',
    isValid: ({carrier}) => !!carrier,
    Component: function ShippingStep({request, options, color, onChange}) {
        return (
            <>
                <StepTitle title='Hoe verstuur je het pakket?' subtitle='Je levert het pakket in bij een servicepunt van de gekozen vervoerder.' />

                {options.carriers.map((carrier) => (
                    <OptionTile
                        key={carrier.name}
                        color={color}
                        selected={request.carrier === carrier.name}
                        onClick={() => onChange({...request, carrier: carrier.name})}
                    >
                        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                            {CARRIER_LOGOS[carrierDisplayName(carrier.name)] &&
                                <img src={CARRIER_LOGOS[carrierDisplayName(carrier.name)]} alt='' style={{height: 28, width: 48, objectFit: 'contain'}} />
                            }
                            <div style={{flex: 1, fontWeight: 500, color: TEXT.strong}}>{carrierDisplayName(carrier.name)}</div>
                            <div style={{fontWeight: 600, color: TEXT.strong}}>{formatCosts(carrier.shippingCosts)}</div>
                        </div>
                    </OptionTile>
                ))}

                {!!options.pickupPoints.length && request.carrier &&
                    <PickupPointMap pickupPoints={options.pickupPoints} carrier={request.carrier} center={request.address.position} />
                }
            </>
        )
    }
}
